// src/taskcreator/TaskCreator.tsx
import { useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, storage } from "../firebase";
import { ref, getDownloadURL } from "firebase/storage";
import { extractTextFromFile } from "../course/fileTextExtractor";
import {
    Settings,
    FileText,
    Brain,
    Download,
    Plus,
    Minus,
    Sparkles,
    BookOpen,
    Target,
    Palette,
    Zap,
    CheckCircle,
    Clock,
    Users,
    TrendingUp,
    FileType,
    FileDown,
    Youtube,
    AlertCircle,
    Check,
    X,
    Link,
    LayoutTemplate,
    Grid3x3,
    ListChecks,
    Type,
    PenTool,
    FileCheck
} from "lucide-react";

/* =========================
   TYPES
========================= */
export type FileItem = {
    name: string;
    path: string;
};

export type TaskCreatorProps = {
    defaultTopic?: string;
    files?: FileItem[];
    templateMode?: boolean;
    templateName?: string;
    templateId?: string;
    onGenerateWithTemplate?: (
        topic: string,
        context: string,
        difficulty: string,
        materialText: string,
        numSingle: number,
        numMultiple: number,
        numFree: number,
        numGap: number,
        files?: FileItem[],
        contentSource?: "manual" | "youtube",
        youtubeUrl?: string
    ) => Promise<{ success?: boolean; error?: string; taskCount?: number; analysis?: any }>;
    customPlaceholders?: Record<string, string>;
    onPlaceholdersChange?: (placeholders: Record<string, string>) => void;
};

export type FileFormat = "docx" | "pdf";
export type ContentSource = "manual" | "youtube";

/* =========================
   SUB COMPONENT - TaskTypeCard
========================= */
interface TaskTypeCardProps {
    type: string;
    label: string;
    description: string;
    value: number;
    onChange: (value: number) => void;
    color: string;
    icon: React.ReactNode;
}

function TaskTypeCard({ label, description, value, onChange, color, icon }: TaskTypeCardProps) {
    return (
        <div
            className="bg-gray-50/80 border-l-4 p-5 rounded-xl border border-gray-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
            style={{ borderLeftColor: color }}
        >
            <div className="flex items-start gap-3 mb-4">
                <div className="text-2xl" style={{ color }}>{icon}</div>
                <div className="flex-1">
                    <h4 className="text-base font-bold text-gray-800 mb-1">{label}</h4>
                    <p className="text-sm text-gray-500">{description}</p>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <button
                    onClick={() => onChange(Math.max(0, value - 1))}
                    disabled={value <= 0}
                    className="w-9 h-9 rounded-lg border-2 border-gray-300 bg-white flex items-center justify-center text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:border-green-500 hover:text-green-500 transition-colors"
                >
                    <Minus size={16} />
                </button>
                <input
                    type="number"
                    min={0}
                    max={10}
                    value={value}
                    onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val)) onChange(Math.min(10, Math.max(0, val)));
                    }}
                    className="flex-1 py-2 text-center text-lg font-bold border-2 rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500/50"
                    style={{ borderColor: color }}
                />
                <button
                    onClick={() => onChange(Math.min(10, value + 1))}
                    disabled={value >= 10}
                    className="w-9 h-9 rounded-lg border-2 border-gray-300 bg-white flex items-center justify-center text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:border-green-500 hover:text-green-500 transition-colors"
                >
                    <Plus size={16} />
                </button>
            </div>
        </div>
    );
}

/* =========================
   MAIN COMPONENT - TaskCreator
========================= */
export default function TaskCreator({
                                        defaultTopic = "",
                                        files = [],
                                        templateMode = false,
                                        templateName = "",
                                        templateId = "",
                                        onGenerateWithTemplate,
                                        customPlaceholders = {},
                                        onPlaceholdersChange
                                    }: TaskCreatorProps) {
    const [topic, setTopic] = useState(defaultTopic);
    const [context, setContext] = useState("");
    const [difficulty, setDifficulty] = useState("mittel");
    const [fileFormat, setFileFormat] = useState<FileFormat>("docx");
    const [contentSource, setContentSource] = useState<ContentSource>("manual");
    const [youtubeUrl, setYoutubeUrl] = useState("");
    const [youtubeInfo, setYoutubeInfo] = useState<any>(null);
    const [checkingYoutube, setCheckingYoutube] = useState(false);
    const [placeholders, setPlaceholders] = useState<Record<string, string>>(customPlaceholders);

    const [numSingle, setNumSingle] = useState(2);
    const [numMultiple, setNumMultiple] = useState(1);
    const [numFree, setNumFree] = useState(1);
    const [numGap, setNumGap] = useState(1);

    const [loading, setLoading] = useState(false);
    const [debugInfo, setDebugInfo] = useState<string>("");
    const [activeTab, setActiveTab] = useState<"config" | "preview" | "materials" | "youtube" | "placeholders">("config");
    const [generationStats, setGenerationStats] = useState<{
        success?: boolean;
        taskCount?: number;
        fileName?: string;
    }>({});

    /* =========================
       Default Topic übernehmen
    ========================= */
    useEffect(() => {
        if (defaultTopic) {
            setTopic(defaultTopic);
        }
    }, [defaultTopic]);

    /* =========================
       YouTube URL prüfen
    ========================= */
    const checkYouTubeUrl = async () => {
        if (!youtubeUrl.trim()) {
            setDebugInfo("❌ Bitte eine YouTube-URL eingeben");
            return;
        }

        setCheckingYoutube(true);
        setDebugInfo("");

        try {
            const result: any = await httpsCallable(
                getFunctions(app),
                "getYouTubeInfo"
            )({
                youtubeUrl: youtubeUrl.trim(),
            });

            console.log("YouTube Info erhalten:", result.data);

            if (result.data?.error) {
                setDebugInfo(`❌ YouTube-Fehler: ${result.data.error}`);
                setYoutubeInfo(null);
            } else {
                setYoutubeInfo(result.data);
                if (result.data.hasTranscript) {
                    setDebugInfo(`✅ YouTube-Video gefunden: "${result.data.title}" (${result.data.transcriptLength} Zeichen Transkript)`);
                } else {
                    setDebugInfo(`⚠️ YouTube-Video gefunden: "${result.data.title}" (Kein Transkript verfügbar)`);
                }
            }
        } catch (err: any) {
            console.error("YouTube Check Fehler:", err);
            setDebugInfo(`❌ Fehler beim Prüfen der YouTube-URL: ${err.message}`);
            setYoutubeInfo(null);
        } finally {
            setCheckingYoutube(false);
        }
    };

    /* =========================
       Materialien extrahieren
    ========================= */
    const getAllMaterialText = async () => {
        if (!files.length) return "";

        let combinedText = "";
        let fileCount = 0;

        for (const file of files) {
            try {
                const storageRef = ref(storage, file.path);
                const url = await getDownloadURL(storageRef);
                const text = await extractTextFromFile(url, file.name);
                combinedText += `=== Datei: ${file.name} ===\n${text}\n\n`;
                fileCount++;
            } catch (err) {
                console.warn(
                    `Material konnte nicht gelesen werden: ${file.name}`,
                    err
                );
            }
        }

        console.log(`${fileCount} Dateien extrahiert, Länge: ${combinedText.length} Zeichen`);
        return combinedText;
    };

    /* =========================
       Generate Tasks
    ========================= */
    const generateTasks = async () => {
        if (!topic.trim()) {
            setDebugInfo("❌ Bitte ein Thema angeben.");
            return;
        }

        if (contentSource === "youtube" && !youtubeUrl.trim() && !templateMode) {
            setDebugInfo("❌ Bitte eine YouTube-URL eingeben.");
            return;
        }

        setLoading(true);
        setDebugInfo("");
        setGenerationStats({});

        try {
            const materialText = await getAllMaterialText();

            if (files.length > 0) {
                setDebugInfo(`📚 Materialien geladen: ${materialText.length} Zeichen aus ${files.length} Dateien`);
            }

            if (templateMode && onGenerateWithTemplate) {
                console.log("📋 Template-Modus aktiviert:", {
                    templateName,
                    templateId,
                    topic,
                    difficulty,
                    taskCounts: {
                        single: numSingle,
                        multiple: numMultiple,
                        free: numFree,
                        gap: numGap
                    }
                });

                const result = await onGenerateWithTemplate(
                    topic.trim(),
                    context.trim(),
                    difficulty,
                    materialText,
                    numSingle,
                    numMultiple,
                    numFree,
                    numGap,
                    files,
                    contentSource,
                    youtubeUrl.trim()
                );

                if (result.success) {
                    setDebugInfo(`✅ ${result.taskCount || 'Mehrere'} Aufgaben erfolgreich mit Vorlage "${templateName}" generiert!`);
                    setGenerationStats({
                        success: true,
                        taskCount: result.taskCount,
                        fileName: `${topic.replace(/[^a-z0-9]/gi, '_')}.docx`
                    });
                    setTimeout(() => setDebugInfo(""), 5000);
                } else if (result.error) {
                    setDebugInfo(`❌ Fehler: ${result.error}`);
                } else if (result.analysis) {
                    setDebugInfo(`✅ ${result.taskCount || 'Mehrere'} Aufgaben mit KI-Analyse generiert!`);
                    setGenerationStats({
                        success: true,
                        taskCount: result.taskCount
                    });
                    setTimeout(() => setDebugInfo(""), 5000);
                }

            } else {
                if (contentSource === "youtube") {
                    const functionName = fileFormat === "pdf"
                        ? "generateTasksFromYouTubePdf"
                        : "generateTasksFromYouTube";

                    console.log("🎬 YouTube-Generierung starten:", {
                        functionName,
                        topic,
                        youtubeUrl,
                        difficulty
                    });

                    const result: any = await httpsCallable(
                        getFunctions(app),
                        functionName
                    )({
                        youtubeUrl: youtubeUrl.trim(),
                        topic: topic.trim(),
                        context: context.trim(),
                        difficulty,
                        numSingle,
                        numMultiple,
                        numFree,
                        numGap,
                    });

                    console.log("YouTube-Ergebnis erhalten:", result.data);

                    if (result.data?.file) {
                        const mimeType = fileFormat === "pdf"
                            ? "application/pdf"
                            : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

                        const blob = b64toBlob(result.data.file, mimeType);
                        const url = URL.createObjectURL(blob);

                        const fileName = `Arbeitsblatt_${topic.replace(/[^a-z0-9]/gi, '_')}_YouTube.${fileFormat}`;
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = fileName;
                        a.click();

                        URL.revokeObjectURL(url);

                        setDebugInfo(`✅ Aufgaben erfolgreich aus YouTube-Video generiert als ${fileFormat.toUpperCase()}!`);
                        setGenerationStats({
                            success: true,
                            taskCount: numSingle + numMultiple + numFree + numGap,
                            fileName
                        });
                        setTimeout(() => setDebugInfo(""), 5000);
                    } else if (result.data?.error) {
                        setDebugInfo(`❌ Fehler: ${result.data.error}`);
                    } else {
                        setDebugInfo("❌ Unbekannter Fehler beim Erstellen der Datei.");
                    }
                } else {
                    console.log("🤖 Normale Aufgaben-Generierung starten:", {
                        topic,
                        difficulty,
                        taskCounts: {
                            single: numSingle,
                            multiple: numMultiple,
                            free: numFree,
                            gap: numGap
                        },
                        materialLength: materialText.length
                    });

                    const functionName = fileFormat === "pdf" ? "generateTasksPdf" : "generateTasks";

                    const result: any = await httpsCallable(
                        getFunctions(app),
                        functionName
                    )({
                        topic: topic.trim(),
                        context: context.trim(),
                        difficulty,
                        numSingle,
                        numMultiple,
                        numFree,
                        numGap,
                        materialText,
                    });

                    console.log("Ergebnis erhalten:", result.data);

                    if (result.data?.file) {
                        const mimeType = fileFormat === "pdf"
                            ? "application/pdf"
                            : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

                        const blob = b64toBlob(result.data.file, mimeType);
                        const url = URL.createObjectURL(blob);

                        const fileName = `Arbeitsblatt_${topic.replace(/[^a-z0-9]/gi, '_')}.${fileFormat}`;
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = fileName;
                        a.click();

                        URL.revokeObjectURL(url);

                        setDebugInfo(`✅ Aufgaben erfolgreich als ${fileFormat.toUpperCase()} generiert und heruntergeladen!`);
                        setGenerationStats({
                            success: true,
                            taskCount: numSingle + numMultiple + numFree + numGap,
                            fileName
                        });
                        setTimeout(() => setDebugInfo(""), 5000);
                    } else if (result.data?.error) {
                        setDebugInfo(`❌ Fehler: ${result.data.error}`);
                    } else {
                        setDebugInfo("❌ Unbekannter Fehler beim Erstellen der Datei.");
                    }
                }
            }
        } catch (err: any) {
            console.error("Fehler:", err);
            setDebugInfo(`❌ Serverfehler: ${err?.message || JSON.stringify(err)}`);
        }

        setLoading(false);
    };

    /* =========================
       Helpers
    ========================= */
    const b64toBlob = (base64: string, type: string) => {
        try {
            const byteCharacters = atob(base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            return new Blob([new Uint8Array(byteNumbers)], { type });
        } catch (err) {
            console.error("Fehler beim Konvertieren von Base64 zu Blob:", err);
            throw new Error("Dateikonvertierung fehlgeschlagen");
        }
    };

    /* =========================
       Reset zu Standardwerten
    ========================= */
    const resetToDefaults = () => {
        setNumSingle(2);
        setNumMultiple(1);
        setNumFree(1);
        setNumGap(1);
        setDifficulty("mittel");
        setContext("");
        setFileFormat("docx");
        setContentSource("manual");
        setYoutubeUrl("");
        setYoutubeInfo(null);
        setDebugInfo("");
        setPlaceholders({});
        setGenerationStats({});
        if (onPlaceholdersChange) {
            onPlaceholdersChange({});
        }
    };

    /* =========================
       Placeholders Tab
    ========================= */
    const renderPlaceholdersTab = () => (
        <div className="space-y-6 animate-fade-in">
            <div className="flex items-center gap-3">
                <Grid3x3 size={24} className="text-purple-500" />
                <h3 className="text-xl font-bold text-gray-800">Platzhalter verwalten</h3>
            </div>

            <div className="bg-gray-50/80 border border-gray-200 rounded-xl p-5">
                <p className="text-gray-600 mb-4">
                    Füge benutzerdefinierte Platzhalter hinzu, die in deiner Vorlage verwendet werden.
                    Beispiel: Wenn deine Vorlage <code className="bg-gray-100 px-2 py-1 rounded text-gray-800">{"{{klasse}}"}</code> enthält,
                    trage hier "klasse" und den gewünschten Wert ein.
                </p>

                <div className="space-y-4">
                    <div className="flex items-center gap-3">
                        <input
                            type="text"
                            placeholder="Platzhalter-Name (ohne {{ }})"
                            className="flex-1 px-4 py-3 bg-white border-2 border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-green-500"
                            id="placeholder-name"
                        />
                        <input
                            type="text"
                            placeholder="Wert"
                            className="flex-1 px-4 py-3 bg-white border-2 border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-green-500"
                            id="placeholder-value"
                        />
                        <button
                            onClick={() => {
                                const nameInput = document.getElementById('placeholder-name') as HTMLInputElement;
                                const valueInput = document.getElementById('placeholder-value') as HTMLInputElement;
                                const name = nameInput.value.trim();
                                const value = valueInput.value.trim();

                                if (name && value) {
                                    const newPlaceholders = { ...placeholders, [name]: value };
                                    setPlaceholders(newPlaceholders);
                                    if (onPlaceholdersChange) {
                                        onPlaceholdersChange(newPlaceholders);
                                    }
                                    nameInput.value = '';
                                    valueInput.value = '';
                                    setDebugInfo(`✅ Platzhalter "${name}" hinzugefügt`);
                                }
                            }}
                            className="bg-purple-600 text-white px-4 py-3 rounded-xl font-semibold hover:bg-purple-700 transition-all flex items-center gap-2"
                        >
                            <Plus size={16} />
                            Hinzufügen
                        </button>
                    </div>
                </div>
            </div>

            {Object.keys(placeholders).length > 0 ? (
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="p-4 bg-gray-100/50 border-b border-gray-200">
                        <h4 className="font-semibold text-gray-800">Aktive Platzhalter</h4>
                    </div>
                    <div className="divide-y divide-gray-200">
                        {Object.entries(placeholders).map(([key, value]) => (
                            <div key={key} className="p-4 flex items-center justify-between">
                                <div>
                                    <code className="text-green-600 font-mono text-sm">{"{{" + key + "}}"}</code>
                                    <div className="text-sm text-gray-500 mt-1">Wert: {value}</div>
                                </div>
                                <button
                                    onClick={() => {
                                        const newPlaceholders = { ...placeholders };
                                        delete newPlaceholders[key];
                                        setPlaceholders(newPlaceholders);
                                        if (onPlaceholdersChange) {
                                            onPlaceholdersChange(newPlaceholders);
                                        }
                                        setDebugInfo(`🗑️ Platzhalter "${key}" entfernt`);
                                    }}
                                    className="text-red-500 hover:text-red-400 transition-colors"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="text-center py-8 border border-gray-200 rounded-xl">
                    <Grid3x3 size={48} className="text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Noch keine benutzerdefinierten Platzhalter hinzugefügt.</p>
                </div>
            )}

            <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-5">
                <h4 className="text-lg font-bold text-gray-800 mb-3">💡 Tipps zu Platzhaltern</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                        <span className="text-purple-500">•</span>
                        <span>Platzhalter in der Vorlage müssen exakt übereinstimmen (Groß-/Kleinschreibung beachten)</span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="text-purple-500">•</span>
                        <span>Standard-Platzhalter: <code className="bg-gray-100 px-1 rounded text-gray-800">{"{{title}}"}</code>, <code className="bg-gray-100 px-1 rounded text-gray-800">{"{{date}}"}</code>, <code className="bg-gray-100 px-1 rounded text-gray-800">{"{{tasks}}"}</code></span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="text-purple-500">•</span>
                        <span>Die KI ersetzt Platzhalter automatisch - du musst nichts in der Vorlage ändern</span>
                    </li>
                </ul>
            </div>
        </div>
    );

    /* =========================
       YouTube Content Tab
    ========================= */
    const renderYouTubeTab = () => (
        <div className="space-y-6 animate-fade-in">
            <div className="flex items-center gap-3">
                <Youtube size={24} className="text-red-500" />
                <h3 className="text-xl font-bold text-gray-800">YouTube Video als Quelle</h3>
            </div>

            <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700">
                    YouTube-URL *
                    <span className="block text-xs text-gray-500 font-normal mt-1">
                        Füge die vollständige YouTube-URL ein (z.B. https://www.youtube.com/watch?v=...)
                    </span>
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                    <input
                        value={youtubeUrl}
                        onChange={(e) => setYoutubeUrl(e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="flex-1 px-4 py-3 bg-white border-2 border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/30"
                        disabled={checkingYoutube}
                    />
                    <button
                        onClick={checkYouTubeUrl}
                        disabled={checkingYoutube || !youtubeUrl.trim()}
                        className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 min-w-[140px]"
                    >
                        {checkingYoutube ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Prüfe...
                            </>
                        ) : (
                            <>
                                <Link size={16} />
                                Video prüfen
                            </>
                        )}
                    </button>
                </div>
            </div>

            {youtubeInfo && (
                <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                    <div className="p-5 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row gap-4">
                        <img
                            src={youtubeInfo.thumbnail}
                            alt="Video Thumbnail"
                            className="w-full sm:w-32 h-48 sm:h-20 object-cover rounded-lg"
                        />
                        <div className="flex-1 min-w-0">
                            <h4 className="text-lg font-bold text-gray-800 line-clamp-2">{youtubeInfo.title}</h4>
                            <p className="text-sm text-gray-500 mt-2">{youtubeInfo.channel}</p>
                        </div>
                    </div>

                    <div className="p-5">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${youtubeInfo.hasTranscript ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-yellow-100 text-yellow-700 border border-yellow-200'}`}>
                                {youtubeInfo.hasTranscript ? <Check size={14} /> : <AlertCircle size={14} />}
                            </div>
                            <div>
                                <div className="text-sm text-gray-500">Transkript</div>
                                <div className="text-sm font-semibold text-gray-800">
                                    {youtubeInfo.hasTranscript
                                        ? `${youtubeInfo.transcriptLength} Zeichen verfügbar`
                                        : 'Kein Transkript verfügbar'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {!youtubeInfo.hasTranscript && (
                        <div className="bg-yellow-50 border-t border-yellow-200 p-4 flex gap-3 text-yellow-800">
                            <AlertCircle size={16} className="flex-shrink-0" />
                            <div className="text-sm">
                                <strong>Hinweis:</strong> Dieses Video hat kein verfügbares Transkript.
                                Die KI wird den Video-Titel und Beschreibung verwenden.
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="bg-gray-50/80 border border-gray-200 rounded-xl p-5">
                <h4 className="text-lg font-bold text-gray-800 mb-3">So funktioniert's:</h4>
                <ol className="list-decimal pl-5 space-y-2 text-gray-600">
                    <li>Füge eine YouTube-URL ein und klicke auf "Video prüfen"</li>
                    <li>Das System extrahiert automatisch Untertitel (falls verfügbar)</li>
                    <li>Die KI erstellt Aufgaben basierend auf dem Video-Inhalt</li>
                    <li>Das generierte Arbeitsblatt enthält einen Hinweis auf das Quell-Video</li>
                </ol>
            </div>
        </div>
    );

    /* =========================
       Preview Content
    ========================= */
    const renderPreview = () => {
        const totalTasks = numSingle + numMultiple + numFree + numGap;
        const sourceInfo = contentSource === "youtube" && youtubeInfo
            ? `aus YouTube-Video: "${youtubeInfo.title}"`
            : files.length > 0
                ? `aus ${files.length} Material${files.length !== 1 ? 'ien' : ''}`
                : "basierend auf KI-Wissen";

        return (
            <div className="bg-gray-50/80 border border-gray-200 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-6">
                    <FileText size={24} className="text-gray-700" />
                    <h3 className="text-xl font-bold text-gray-800">Vorschau deines Arbeitsblatts</h3>
                </div>

                <div className="space-y-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <h4 className="text-lg font-bold text-gray-800 mb-2">Arbeitsauftrag: {topic || "[Thema]"}</h4>
                        <p className="text-gray-500 text-sm mb-6 pb-4 border-b border-gray-200">{sourceInfo}</p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            <div className="text-center">
                                <div className="text-3xl font-black text-green-600">{totalTasks}</div>
                                <div className="text-sm text-gray-500">Aufgaben</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-black text-green-600">{numSingle}</div>
                                <div className="text-sm text-gray-500">Single Choice</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-black text-green-600">{numMultiple}</div>
                                <div className="text-sm text-gray-500">Multiple Choice</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-black text-green-600">{numFree + numGap}</div>
                                <div className="text-sm text-gray-500">Freie/Lückentexte</div>
                            </div>
                        </div>

                        <div>
                            <h5 className="text-gray-800 font-semibold mb-3">Beispielaufgaben:</h5>
                            <div className="space-y-3">
                                {numSingle > 0 && (
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <span className="inline-block bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full mb-2">Single Choice</span>
                                        <p className="text-gray-700">Welche der folgenden Aussagen beschreibt am besten...?</p>
                                    </div>
                                )}
                                {numGap > 0 && (
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <span className="inline-block bg-yellow-100 text-yellow-700 text-xs font-semibold px-3 py-1 rounded-full mb-2">Lückentext</span>
                                        <p className="text-gray-700">Fülle die Lücken im Text über {topic || "das Thema"}: _____</p>
                                    </div>
                                )}
                                {templateMode && Object.keys(placeholders).length > 0 && (
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <span className="inline-block bg-purple-100 text-purple-700 text-xs font-semibold px-3 py-1 rounded-full mb-2">Platzhalter</span>
                                        <p className="text-gray-700">
                                            {Object.entries(placeholders).map(([key, value]) => (
                                                <span key={key} className="block">
                                                    <code>{"{{" + key + "}}"}</code>: {value}
                                                </span>
                                            ))}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3 text-gray-600">
                            <CheckCircle size={18} className="text-green-600" />
                            <span className="text-sm">Automatisch generiert</span>
                        </div>
                        <div className="flex items-center gap-3 text-gray-600">
                            <Clock size={18} className="text-blue-600" />
                            <span className="text-sm">Format: {fileFormat.toUpperCase()}</span>
                        </div>
                        <div className="flex items-center gap-3 text-gray-600">
                            <Users size={18} className="text-purple-600" />
                            <span className="text-sm">Angepasst an Schwierigkeit</span>
                        </div>
                        <div className="flex items-center gap-3 text-gray-600">
                            <TrendingUp size={18} className="text-yellow-600" />
                            <span className="text-sm">Didaktisch optimiert</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    /* =========================
       Materials Content
    ========================= */
    const renderMaterials = () => (
        <div className="bg-gray-50/80 border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
                <BookOpen size={24} className="text-gray-700" />
                <h3 className="text-xl font-bold text-gray-800">Materialien</h3>
            </div>

            <div className="min-h-[200px]">
                {files.length > 0 ? (
                    <>
                        <div className="space-y-3 mb-6">
                            {files.map((file, index) => (
                                <div key={index} className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl">
                                    <FileText size={18} className="text-gray-500" />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-semibold text-gray-800 truncate">{file.name}</div>
                                        <div className="text-xs text-gray-500 truncate">{file.path}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xl p-4">
                            <div className="flex gap-3">
                                <Sparkles size={20} className="text-green-500 flex-shrink-0" />
                                <div>
                                    <div className="font-bold text-gray-800">KI-gestützte Aufgaben</div>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Die Aufgaben werden auf Basis dieser Materialien erstellt.
                                        Die KI analysiert den Inhalt und generiert passende Fragen.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12">
                        <BookOpen size={48} className="text-gray-400 mb-4" />
                        <p className="text-gray-500 text-center">
                            Keine Materialien hochgeladen. Die KI verwendet ihr Fachwissen.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );

    /* =========================
       RENDER
    ========================= */
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white font-sans relative overflow-x-hidden">
            {/* Template Badge */}
            {templateMode && (
                <div className="fixed top-20 right-4 z-50">
                    <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-4 py-2 rounded-full font-semibold text-sm shadow-lg flex items-center gap-2 animate-fade-in">
                        <LayoutTemplate size={14} />
                        Vorlagen-Modus: {templateName}
                    </div>
                </div>
            )}

            {/* Background Glow (heller) */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-green-500/20 to-transparent opacity-30 blur-[100px] animate-pulse-glow -z-10" />

            {/* HEADER */}
            <div className="bg-white/80 backdrop-blur-lg border-b border-gray-200 relative z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-6 mb-6">
                        <div className="bg-gradient-to-br from-green-500 to-blue-500 w-16 h-16 rounded-2xl flex items-center justify-center text-white flex-shrink-0">
                            <Brain size={32} />
                        </div>
                        <div className="flex-1">
                            <div className="inline-block bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4 border border-green-200 backdrop-blur-sm">
                                ✨ KI-Aufgabengenerator {templateMode ? "(Mit Vorlage)" : ""}
                            </div>
                            <h1 className="text-3xl md:text-4xl font-black text-gray-800 mb-3">
                                Erstelle <span className="bg-gradient-to-r from-green-500 to-blue-500 bg-clip-text text-transparent">didaktisch wertvolle</span> Arbeitsblätter
                            </h1>
                            <p className="text-gray-600 text-lg">
                                Für den Islam-Unterricht – aus Texten, Materialien oder YouTube-Videos
                                {templateMode && ` – mit Vorlage "${templateName}"`}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium border border-gray-200">
                            <Zap size={14} /> KI-gestützt
                        </span>
                        <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium border border-gray-200">
                            <FileType size={14} /> {templateMode ? "Vorlage" : fileFormat.toUpperCase()}
                        </span>
                        <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium border border-gray-200">
                            <Target size={14} /> Lehrplankonform
                        </span>
                        {contentSource === "youtube" && (
                            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2 rounded-full text-sm font-medium border border-red-500/30">
                                <Youtube size={14} /> YouTube
                            </span>
                        )}
                        {templateMode && (
                            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-full text-sm font-medium border border-purple-500/30">
                                <LayoutTemplate size={14} /> Vorlage
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* MAIN CONTENT */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 relative z-10">
                {/* LEFT PANEL */}
                <div className="lg:col-span-2">
                    <div className="bg-white/80 backdrop-blur-lg border border-gray-200 rounded-2xl overflow-hidden">
                        {/* Tabs */}
                        <div className="flex flex-wrap gap-2 p-2 bg-gray-100/50 border-b border-gray-200">
                            <button
                                className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold transition-all ${activeTab === "config" ? "bg-green-500 text-white shadow-md" : "text-gray-600 hover:text-gray-800 hover:bg-gray-200"}`}
                                onClick={() => setActiveTab("config")}
                            >
                                <Settings size={18} />
                                Konfiguration
                            </button>
                            {!templateMode && (
                                <button
                                    className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold transition-all ${activeTab === "youtube" ? "bg-green-500 text-white shadow-md" : "text-gray-600 hover:text-gray-800 hover:bg-gray-200"}`}
                                    onClick={() => setActiveTab("youtube")}
                                >
                                    <Youtube size={18} />
                                    YouTube
                                </button>
                            )}
                            {templateMode && (
                                <button
                                    className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold transition-all ${activeTab === "placeholders" ? "bg-green-500 text-white shadow-md" : "text-gray-600 hover:text-gray-800 hover:bg-gray-200"}`}
                                    onClick={() => setActiveTab("placeholders")}
                                >
                                    <Grid3x3 size={18} />
                                    Platzhalter
                                </button>
                            )}
                            <button
                                className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold transition-all ${activeTab === "preview" ? "bg-green-500 text-white shadow-md" : "text-gray-600 hover:text-gray-800 hover:bg-gray-200"}`}
                                onClick={() => setActiveTab("preview")}
                            >
                                <FileText size={18} />
                                Vorschau
                            </button>
                            <button
                                className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold transition-all ${activeTab === "materials" ? "bg-green-500 text-white shadow-md" : "text-gray-600 hover:text-gray-800 hover:bg-gray-200"}`}
                                onClick={() => setActiveTab("materials")}
                            >
                                <BookOpen size={18} />
                                Materialien
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="p-4 sm:p-6 max-h-[calc(100vh-300px)] overflow-y-auto">
                            {activeTab === "config" ? (
                                <div className="space-y-8">
                                    {/* Dateiformat nur anzeigen wenn nicht im Template-Modus */}
                                    {!templateMode && (
                                        <section>
                                            <div className="flex items-center gap-3 mb-6">
                                                <div className="bg-green-100 w-10 h-10 rounded-xl flex items-center justify-center text-green-600 flex-shrink-0">
                                                    <FileType size={20} />
                                                </div>
                                                <h3 className="text-xl font-bold text-gray-800">Dateiformat</h3>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <button
                                                    className={`p-4 border-2 rounded-xl flex items-center gap-4 transition-all ${fileFormat === "docx" ? "border-green-500 bg-green-50" : "border-gray-300 bg-white hover:border-gray-400"}`}
                                                    onClick={() => setFileFormat("docx")}
                                                >
                                                    <FileText size={18} className="text-gray-600 flex-shrink-0" />
                                                    <div className="text-left">
                                                        <div className="font-bold text-gray-800">Word (DOCX)</div>
                                                        <div className="text-sm text-gray-500">Bearbeitbar, ideal für Anpassungen</div>
                                                    </div>
                                                </button>
                                                <button
                                                    className={`p-4 border-2 rounded-xl flex items-center gap-4 transition-all ${fileFormat === "pdf" ? "border-green-500 bg-green-50" : "border-gray-300 bg-white hover:border-gray-400"}`}
                                                    onClick={() => setFileFormat("pdf")}
                                                >
                                                    <FileDown size={18} className="text-gray-600 flex-shrink-0" />
                                                    <div className="text-left">
                                                        <div className="font-bold text-gray-800">PDF</div>
                                                        <div className="text-sm text-gray-500">Druckfertig, universell kompatibel</div>
                                                    </div>
                                                </button>
                                            </div>
                                            <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-xl">
                                                {fileFormat === "docx" ? (
                                                    <span>📝 Bearbeitbares Format für individuelle Anpassungen</span>
                                                ) : (
                                                    <span>📄 Druckfertiges Format für direkten Einsatz im Unterricht</span>
                                                )}
                                            </div>
                                        </section>
                                    )}

                                    {/* Inhaltsquelle */}
                                    {!templateMode && (
                                        <section>
                                            <div className="flex items-center gap-3 mb-6">
                                                <div className="bg-green-100 w-10 h-10 rounded-xl flex items-center justify-center text-green-600 flex-shrink-0">
                                                    <Sparkles size={20} />
                                                </div>
                                                <h3 className="text-xl font-bold text-gray-800">Inhaltsquelle wählen</h3>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                                <button
                                                    className={`p-4 border-2 rounded-xl flex items-center gap-4 transition-all ${contentSource === "manual" ? "border-green-500 bg-green-50" : "border-gray-300 bg-white hover:border-gray-400"}`}
                                                    onClick={() => {
                                                        setContentSource("manual");
                                                        setYoutubeUrl("");
                                                        setYoutubeInfo(null);
                                                    }}
                                                >
                                                    <BookOpen size={20} className="text-gray-600 flex-shrink-0" />
                                                    <div className="text-left">
                                                        <div className="font-bold text-gray-800">Manuell</div>
                                                        <div className="text-sm text-gray-500">Eigenes Thema oder hochgeladene Materialien</div>
                                                    </div>
                                                </button>
                                                <button
                                                    className={`p-4 border-2 rounded-xl flex items-center gap-4 transition-all ${contentSource === "youtube" ? "border-green-500 bg-green-50" : "border-gray-300 bg-white hover:border-gray-400"}`}
                                                    onClick={() => setContentSource("youtube")}
                                                >
                                                    <Youtube size={20} className="text-gray-600 flex-shrink-0" />
                                                    <div className="text-left">
                                                        <div className="font-bold text-gray-800">YouTube</div>
                                                        <div className="text-sm text-gray-500">Extrahiert automatisch Untertitel als Grundlage</div>
                                                    </div>
                                                </button>
                                            </div>
                                        </section>
                                    )}

                                    {/* Thema & Kontext */}
                                    <section>
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="bg-green-100 w-10 h-10 rounded-xl flex items-center justify-center text-green-600 flex-shrink-0">
                                                <Type size={20} />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-800">Thema & Kontext</h3>
                                        </div>
                                        <div className="space-y-6">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    Unterrichtsthema *
                                                    <span className="block text-xs text-gray-500 font-normal mt-1">
                                                        Wichtig für präzise Aufgaben
                                                    </span>
                                                </label>
                                                <input
                                                    value={topic}
                                                    onChange={(e) => setTopic(e.target.value)}
                                                    placeholder="z. B. 'Fünf Säulen des Islam', 'Ramadan', 'Propheten'"
                                                    className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/30"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    Zusätzlicher Kontext
                                                    <span className="block text-xs text-gray-500 font-normal mt-1">
                                                        Optional: Klassenstufe, Lernziele, etc.
                                                    </span>
                                                </label>
                                                <textarea
                                                    value={context}
                                                    onChange={(e) => setContext(e.target.value)}
                                                    placeholder="z. B. 'Klassenstufe 7', 'Wiederholungsstufe', 'Vorbereitung auf Klassenarbeit'"
                                                    className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/30 min-h-[100px] resize-y"
                                                />
                                            </div>
                                        </div>
                                    </section>

                                    {/* Schwierigkeitsgrad */}
                                    <section>
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="bg-green-100 w-10 h-10 rounded-xl flex items-center justify-center text-green-600 flex-shrink-0">
                                                <TrendingUp size={20} />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-800">Schwierigkeitsgrad</h3>
                                        </div>
                                        <div className="flex flex-col sm:flex-row gap-3 mb-4">
                                            {["leicht", "mittel", "schwer"].map((level) => (
                                                <button
                                                    key={level}
                                                    className={`flex-1 py-3 px-4 border-2 rounded-xl font-semibold transition-all ${difficulty === level ? "border-green-500 bg-green-50 text-green-700" : "border-gray-300 bg-white text-gray-600 hover:border-gray-400"}`}
                                                    onClick={() => setDifficulty(level)}
                                                >
                                                    {level === "leicht" && "👶 Leicht"}
                                                    {level === "mittel" && "🎯 Mittel"}
                                                    {level === "schwer" && "🧠 Schwer"}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-xl">
                                            {difficulty === "leicht" && (
                                                <span>Einfache Fragen, Basiswissen, kurze Antworten</span>
                                            )}
                                            {difficulty === "mittel" && (
                                                <span>Anspruchsvolle Fragen, Transferwissen, längere Antworten</span>
                                            )}
                                            {difficulty === "schwer" && (
                                                <span>Komplexe Fragen, Analyse und Bewertung, umfangreiche Antworten</span>
                                            )}
                                        </div>
                                    </section>

                                    {/* Aufgabentypen */}
                                    <section>
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="bg-green-100 w-10 h-10 rounded-xl flex items-center justify-center text-green-600 flex-shrink-0">
                                                <ListChecks size={20} />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-800">Aufgabentypen & Anzahl</h3>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                            <TaskTypeCard
                                                type="singleChoice"
                                                label="Single Choice"
                                                description="Eine richtige Antwort aus 4 Optionen"
                                                value={numSingle}
                                                onChange={setNumSingle}
                                                color="#4CAF50"
                                                icon="🔘"
                                            />
                                            <TaskTypeCard
                                                type="multipleChoice"
                                                label="Multiple Choice"
                                                description="Mehrere richtige Antworten möglich"
                                                value={numMultiple}
                                                onChange={setNumMultiple}
                                                color="#2196F3"
                                                icon="☑️"
                                            />
                                            <TaskTypeCard
                                                type="freeText"
                                                label="Freie Antwort"
                                                description="Eigene Formulierungen der Schüler"
                                                value={numFree}
                                                onChange={setNumFree}
                                                color="#8b5cf6"
                                                icon="📝"
                                            />
                                            <TaskTypeCard
                                                type="gapText"
                                                label="Lückentext"
                                                description="Text mit mehreren Lücken zum Ausfüllen"
                                                value={numGap}
                                                onChange={setNumGap}
                                                color="#f59e0b"
                                                icon="🔤"
                                            />
                                        </div>
                                        <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                                            <span className="text-gray-700 font-semibold">Gesamtaufgaben:</span>
                                            <span className="text-3xl font-black text-green-600">
                                                {numSingle + numMultiple + numFree + numGap}
                                            </span>
                                        </div>
                                    </section>
                                </div>
                            ) : activeTab === "youtube" && !templateMode ? (
                                renderYouTubeTab()
                            ) : activeTab === "placeholders" && templateMode ? (
                                renderPlaceholdersTab()
                            ) : activeTab === "preview" ? (
                                renderPreview()
                            ) : (
                                renderMaterials()
                            )}
                        </div>
                    </div>
                </div>

                {/* RIGHT PANEL */}
                <div className="space-y-6">
                    {/* Action Card */}
                    <div className="bg-white/80 backdrop-blur-lg border border-gray-200 rounded-2xl p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-6">Generierung starten</h3>

                        <div className="space-y-4 mb-6 p-4 bg-gray-50 rounded-xl">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Quelle:</span>
                                <strong className="text-gray-800">
                                    {templateMode
                                        ? "Vorlage"
                                        : contentSource === "youtube"
                                            ? (youtubeInfo?.title ? "YouTube" : "YouTube (nicht geprüft)")
                                            : "Manuelle Eingabe"}
                                </strong>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Thema:</span>
                                <strong className="text-gray-800">{topic || "Nicht angegeben"}</strong>
                            </div>
                            {!templateMode && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Format:</span>
                                    <strong className={`px-3 py-1 rounded-full text-xs font-bold ${fileFormat === "docx" ? "bg-blue-100 text-blue-700 border border-blue-200" : "bg-green-100 text-green-700 border border-green-200"}`}>
                                        {fileFormat.toUpperCase()}
                                    </strong>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-gray-600">Schwierigkeit:</span>
                                <strong className="text-gray-800">{difficulty === "leicht" ? "Leicht" : difficulty === "mittel" ? "Mittel" : "Schwer"}</strong>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Aufgaben:</span>
                                <strong className="text-gray-800">{numSingle + numMultiple + numFree + numGap}</strong>
                            </div>
                            {!templateMode && contentSource === "manual" && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Materialien:</span>
                                    <strong className="text-gray-800">{files.length} Datei{files.length !== 1 ? 'en' : ''}</strong>
                                </div>
                            )}
                            {templateMode && Object.keys(placeholders).length > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Platzhalter:</span>
                                    <strong className="text-gray-800">{Object.keys(placeholders).length} aktiv</strong>
                                </div>
                            )}
                        </div>

                        {contentSource === "youtube" && !youtubeInfo && !templateMode && (
                            <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg text-yellow-800 mb-4">
                                <AlertCircle size={16} />
                                <span className="text-sm">Bitte zuerst die YouTube-URL prüfen</span>
                            </div>
                        )}

                        {templateMode && Object.keys(placeholders).length > 0 && (
                            <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg text-purple-800 mb-4">
                                <Grid3x3 size={16} />
                                <span className="text-sm">{Object.keys(placeholders).length} benutzerdefinierte Platzhalter aktiv</span>
                            </div>
                        )}

                        <button
                            onClick={generateTasks}
                            disabled={loading || !topic.trim() || (contentSource === "youtube" && !youtubeUrl.trim() && !templateMode)}
                            className={`w-full py-4 px-6 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-3 ${loading ? "opacity-80 cursor-wait" : ""} ${!topic.trim() || (contentSource === "youtube" && !youtubeUrl.trim() && !templateMode) ? "opacity-50 cursor-not-allowed" : "bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 hover:-translate-y-1 hover:shadow-xl hover:shadow-green-500/40"}`}
                        >
                            {loading ? (
                                <>
                                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    {templateMode ? "Generiere mit Vorlage..." : `Generiere ${fileFormat.toUpperCase()}...`}
                                </>
                            ) : (
                                <>
                                    <Download size={20} />
                                    {templateMode ? `Mit Vorlage generieren` : `Als ${fileFormat.toUpperCase()} generieren & herunterladen`}
                                </>
                            )}
                        </button>

                        {!topic.trim() && (
                            <div className="text-red-600 text-sm text-center mt-3">
                                Bitte gib ein Thema an, um Aufgaben zu generieren.
                            </div>
                        )}

                        {contentSource === "youtube" && !youtubeUrl.trim() && !templateMode && (
                            <div className="text-red-600 text-sm text-center mt-3">
                                Bitte gib eine YouTube-URL ein.
                            </div>
                        )}

                        {generationStats.success && (
                            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl">
                                <div className="flex items-center gap-2 text-green-700 mb-1">
                                    <FileCheck size={16} />
                                    <span className="font-semibold">Erfolgreich generiert!</span>
                                </div>
                                <div className="text-sm text-gray-600">
                                    {generationStats.taskCount && `${generationStats.taskCount} Aufgaben erstellt`}
                                    {generationStats.fileName && ` • ${generationStats.fileName}`}
                                </div>
                            </div>
                        )}

                        <button
                            onClick={resetToDefaults}
                            className="w-full mt-4 py-3 px-4 border-2 border-gray-300 rounded-xl text-gray-600 font-semibold hover:border-green-500 hover:text-green-600 transition-all flex items-center justify-center gap-2"
                        >
                            <Settings size={16} />
                            Zurücksetzen
                        </button>
                    </div>

                    {/* Debug Info */}
                    {debugInfo && (
                        <div className="bg-white/80 backdrop-blur-lg border border-gray-200 rounded-2xl p-5">
                            <div className="flex justify-between items-center mb-3">
                                <span className="text-sm font-semibold text-gray-500">Status</span>
                                {debugInfo.includes("✅") && <Check size={16} className="text-green-600" />}
                                {debugInfo.includes("❌") && <X size={16} className="text-red-600" />}
                                {debugInfo.includes("⚠️") && <AlertCircle size={16} className="text-yellow-600" />}
                            </div>
                            <div className="text-sm break-words">
                                {debugInfo.includes("✅") ? (
                                    <span className="text-green-600 font-semibold">{debugInfo}</span>
                                ) : debugInfo.includes("❌") ? (
                                    <span className="text-red-600 font-semibold">{debugInfo}</span>
                                ) : debugInfo.includes("⚠️") ? (
                                    <span className="text-yellow-600 font-semibold">{debugInfo}</span>
                                ) : (
                                    <span className="text-gray-700">{debugInfo}</span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tips */}
                    <div className="bg-gradient-to-br from-green-500/10 to-blue-500/10 border border-green-500/20 rounded-2xl p-6">
                        <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-3">
                            <Sparkles size={18} className="text-green-600" />
                            Tipps für beste Ergebnisse
                        </h4>
                        <ul className="space-y-3 text-gray-600">
                            <li className="flex items-start gap-2">
                                <span className="text-green-600">•</span>
                                <span className="text-sm">Spezifische Themen führen zu besseren Aufgaben</span>
                            </li>
                            {!templateMode && (
                                <li className="flex items-start gap-2">
                                    <span className="text-green-600">•</span>
                                    <span className="text-sm">{fileFormat === "docx" ? "Word: Ideal für individuelle Anpassungen" : "PDF: Ideal für direkten Einsatz"}</span>
                                </li>
                            )}
                            {templateMode && (
                                <li className="flex items-start gap-2">
                                    <span className="text-green-600">•</span>
                                    <span className="text-sm">Die KI erkennt automatisch Platzhalter in deiner Vorlage</span>
                                </li>
                            )}
                            <li className="flex items-start gap-2">
                                <span className="text-green-600">•</span>
                                <span className="text-sm">Bei YouTube: Videos mit Untertiteln funktionieren am besten</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-green-600">•</span>
                                <span className="text-sm">Mix aus Aufgabentypen für Abwechslung</span>
                            </li>
                        </ul>
                    </div>

                    {/* Template Info */}
                    {templateMode && (
                        <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-6">
                            <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-3">
                                <LayoutTemplate size={18} className="text-purple-600" />
                                Vorlagen-Info
                            </h4>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600">Vorlage:</span>
                                    <strong className="text-gray-800">{templateName}</strong>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600">ID:</span>
                                    <code className="text-sm text-purple-600">{templateId.substring(0, 12)}...</code>
                                </div>
                                <p className="text-sm text-gray-600 mt-3">
                                    Die KI analysiert automatisch die Struktur deiner Vorlage und erkennt Platzhalter.
                                    Du kannst beliebige Word-Vorlagen verwenden.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* LOADING OVERLAY */}
            {loading && (
                <div className="fixed inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white p-8 rounded-3xl text-center max-w-md w-full mx-4 border border-gray-200 shadow-xl animate-fade-in">
                        <div className="w-16 h-16 border-4 border-gray-300 border-t-green-500 rounded-full animate-spin mx-auto mb-6" />
                        <h3 className="text-2xl font-bold text-gray-800 mb-3">KI generiert deine Aufgaben</h3>
                        <p className="text-gray-600 mb-6">
                            Dies kann 15-30 Sekunden dauern.
                            {contentSource === "youtube" && " YouTube-Transkript wird verarbeitet..."}
                            {templateMode && ` Vorlage "${templateName}" wird analysiert...`}
                            {!templateMode && ` Format: ${fileFormat.toUpperCase()}`}
                        </p>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-green-500 to-blue-500 w-1/2 animate-pulse" />
                        </div>
                        <div className="mt-4 text-sm text-gray-500">
                            {templateMode ? "Die KI erkennt automatisch Platzhalter und Struktur." : "Die KI analysiert dein Thema und Materialien."}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}